import type { IncomeSource, MonthlyLedgerEntry } from '@/types'

export interface PaymentResult {
  /** 'conflict' = another device changed this payment first; nothing was written. */
  status: 'ok' | 'conflict'
  /** The allocation's actual payment as stored now. */
  actualPayment: number | null
  /** The debt's balance as stored now. */
  balance: number
}

/** Thrown by `logPayment` when migration 007 isn't applied — callers fall back. */
export class PaymentRpcUnavailableError extends Error {}

/**
 * Persistence abstraction for the Cash Flow module.
 * Two collections: persistent income sources, and monthly ledger entries.
 *
 * This is the second seam in the codebase that will be swapped for a
 * Supabase-backed implementation when the app moves to SaaS mode.
 */
export interface ILedgerRepository {
  // ─── Income sources ────────────────────────────────────────────────
  getAllIncomeSources(): Promise<IncomeSource[]>
  getIncomeSourceById(id: string): Promise<IncomeSource | null>
  upsertIncomeSource(source: IncomeSource): Promise<IncomeSource>
  deleteIncomeSource(id: string): Promise<void>
  /** Replace every income source for the current workspace. Used by the
   *  local→cloud migration import flow. */
  bulkReplaceIncomeSources(sources: IncomeSource[]): Promise<void>

  // ─── Ledger entries ────────────────────────────────────────────────
  getAllLedgerEntries(): Promise<MonthlyLedgerEntry[]>
  getLedgerEntryByMonth(month: string): Promise<MonthlyLedgerEntry | null>
  upsertLedgerEntry(entry: MonthlyLedgerEntry): Promise<MonthlyLedgerEntry>
  /**
   * v0.8.56 — save an everyday edit to one month. Cloud sends only the rows
   * and fields that differ between `before` and `after` so another
   * household member's saves to other rows survive; falls back to a full
   * upsert when a month-level field changed. Local just stores `after`.
   */
  saveLedgerChanges(before: MonthlyLedgerEntry, after: MonthlyLedgerEntry): Promise<void>
  /**
   * v0.8.56 — atomic payment (cloud only; see migration 007). Sets the
   * allocation's actual payment and moves the debt balance by the
   * difference in one transaction, refusing if the stored actual isn't
   * `expected`. Absent on local repos: one device, nothing to race.
   */
  logPayment?(
    entryId: string,
    debtId: string,
    expected: number | null,
    next: number | null
  ): Promise<PaymentResult>
  deleteLedgerEntry(id: string): Promise<void>
  /** Replace every ledger entry (and its allocations + income rows) for
   *  the current workspace. Used by the local→cloud migration. */
  bulkReplaceLedgerEntries(entries: MonthlyLedgerEntry[]): Promise<void>
}
